/*
 * qikify-hoppy-bridge.js  -  Roastfuel
 * -----------------------------------------------------------------------------
 * Makes Qikify Product Options (tcustomizer / tepo) and Hoppy Appointments
 * (futureblink) work as one flow on appointment products.
 *
 * What it does:
 *  1. BLOCK    - "Book now" (and the theme's sticky Add to cart) refuse to open
 *                the booking modal while a required Qikify option is unselected.
 *                Qikify's own error styling is used so it looks native.
 *  2. TRANSFER - When the booking modal's final Confirm step fires Hoppy's
 *                POST /cart/add.js, the customer's Qikify selections are merged
 *                into that same line item's properties, so the booking AND the
 *                options land on one product in the cart.
 *  3. UNIFY    - The theme's sticky "Add to cart" bar no longer bypasses the
 *                booking flow; it opens the booking modal instead.
 *
 * Nothing here depends on either app's internals beyond documented/observable
 * surface: it degrades to "previous behaviour" if either app is absent.
 */
(function () {
  'use strict';

  var NS = '[qikify-hoppy]';
  var DEBUG = /[?&]qhdebug/.test(window.location.search);
  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, [NS].concat([].slice.call(arguments))); } catch (e) {}
  }
  function warn() {
    try { console.warn.apply(console, [NS].concat([].slice.call(arguments))); } catch (e) {}
  }

  if (window.__qikifyHoppyBridge) { log('already installed'); return; }
  window.__qikifyHoppyBridge = true;

  /* --------------------------------------------------------------------- */
  /* Selectors                                                             */
  /* --------------------------------------------------------------------- */

  var SEL = {
    apptForm:    'form[data-futureblink-appointment="true"]',
    qikifyData:  '.tcustomizer-data',
    qikifyField: '.tcustomizer-field',
    requiredCls: 'tcustomizer-required',
    bookBtn:     '.futureblink-booking-button',
    fab:         '.futureblink-fab',
    stickyBtn:   '.sticky-add-to-cart__button',
    propInput:   'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]'
  };

  var ARM_TTL_MS = 45 * 60 * 1000; /* a booking can legitimately take a while */

  /* --------------------------------------------------------------------- */
  /* Small helpers                                                         */
  /* --------------------------------------------------------------------- */

  function closest(el, sel) {
    while (el && el.nodeType === 1) {
      if (el.matches && el.matches(sel)) return el;
      el = el.parentElement;
    }
    return null;
  }

  function all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function isVisible(el) {
    if (!el) return false;
    if (el.offsetParent !== null) return true;
    var cs = window.getComputedStyle(el);
    return cs && cs.display !== 'none' && cs.visibility !== 'hidden' && cs.position === 'fixed';
  }

  /* Appointment product forms that also carry Qikify options. */
  function bridgedForms() {
    return all(SEL.apptForm).filter(function (f) {
      return !!f.querySelector(SEL.qikifyData);
    });
  }

  /* The single relevant form, when the page has exactly one. */
  function soleForm() {
    var forms = bridgedForms();
    return forms.length === 1 ? forms[0] : null;
  }

  function formFor(el) {
    return closest(el, SEL.apptForm) || soleForm();
  }

  function variantIdOf(form) {
    var input = form && form.querySelector('[name="id"]');
    return input ? String(input.value) : null;
  }

  /* --------------------------------------------------------------------- */
  /* 1. Reading the Qikify selections                                      */
  /* --------------------------------------------------------------------- */

  /*
   * Qikify keeps a live, authoritative mirror of every selection in hidden
   * 'properties[...]' inputs inside .tcustomizer-data. Reading those is both
   * simpler and more robust than walking its widgets.
   */
  function collectQikifyProps(form) {
    var props = {};
    if (!form) return props;
    all(SEL.propInput, form).forEach(function (el) {
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      var m = /^properties\[(.+)\]$/.exec(el.name || '');
      if (!m) return;
      var key = m[1];
      var val = el.value == null ? '' : String(el.value);
      /* Drop empty customer-facing options, keep Qikify's own _internal keys
         (_itemKey / _optionSetId) which it needs to render the cart line. */
      if (val === '' && key.charAt(0) !== '_') return;
      props[key] = val;
    });
    return props;
  }

  /* --------------------------------------------------------------------- */
  /* 2. Validation                                                          */
  /* --------------------------------------------------------------------- */

  /*
   * Which fields the merchant marked required. Qikify only adds its
   * 'tcustomizer-required' CLASS after a failed validation, so that class is an
   * error highlight, not a marker we can read up front. The option-set data it
   * publishes on window._TC is the authoritative, silent source.
   */
  function requiredFields(form) {
    try {
      var sets = window._TC && window._TC.optionSets;
      if (!sets) return null;
      var setInput = form.querySelector('input[name="properties[_optionSetId]"]');
      var keys = (setInput && setInput.value) ? [String(setInput.value)] : Object.keys(sets);
      var out = [];
      keys.forEach(function (k) {
        var set = sets[k];
        if (!set || !Array.isArray(set.fields)) return;
        set.fields.forEach(function (f) {
          if (f && f.required) out.push({ id: String(f.id), label: String(f.label == null ? '' : f.label) });
        });
      });
      return out;
    } catch (e) {
      return null;
    }
  }

  function fieldElement(form, id) {
    return form.querySelector('[id^="tcustomizer-field-' + id + '-"]') ||
           form.querySelector('.tcustomizer-field-' + id);
  }

  /* A field counts as answered when its mirrored property carries a value. */
  function propertyValue(form, label) {
    var wanted = 'properties[' + label + ']';
    var hit = null;
    all(SEL.propInput, form).forEach(function (el) {
      if (hit !== null) return;
      if (el.name === wanted) hit = String(el.value == null ? '' : el.value);
    });
    return hit;
  }

  function domHasAnswer(field) {
    var filled = false;
    all('input, select, textarea', field).forEach(function (i) {
      if (filled) return;
      if (i.type === 'checkbox' || i.type === 'radio') {
        if (i.checked) filled = true;
      } else if (i.classList.contains('tcustomizer-quantity-input')) {
        /* quantity steppers are not the answer to a required question */
      } else if (String(i.value || '').trim() !== '') {
        filled = true;
      }
    });
    return filled;
  }

  /*
   * Silent check, safe to run on every keystroke. Returns the first required
   * field that is visible (i.e. not switched off by Qikify's conditional
   * rules) and still unanswered, or null.
   */
  function firstIncompleteField(form) {
    if (!form) return null;
    var reqs = requiredFields(form);
    var found = null;

    if (reqs && reqs.length) {
      reqs.forEach(function (r) {
        if (found) return;
        var el = fieldElement(form, r.id);
        if (!el || !isVisible(el)) return;
        var val = r.label ? propertyValue(form, r.label) : null;
        var answered = (val !== null) ? String(val).trim() !== '' : domHasAnswer(el);
        if (!answered) found = el;
      });
      return found;
    }

    /* Fallback when the option-set data is unavailable: Qikify's own error
       class (set after a failed validation) plus the "*" required marker. */
    all(SEL.qikifyField, form).forEach(function (field) {
      if (found) return;
      if (!isVisible(field)) return;
      var heading = field.querySelector('.tcustomizer-field__heading');
      var marked = field.classList.contains(SEL.requiredCls) ||
                   (heading && /\*\s*$/.test((heading.textContent || '').trim()));
      if (!marked) return;
      if (!domHasAnswer(field)) found = field;
    });
    return found;
  }

  /*
   * Authoritative check, used on click. Qikify exposes
   * _TC.customizerValidationAPI(show) which throws the shop's own configured
   * error text and paints its own error UI. Fall back to the DOM check.
   */
  function validate(form) {
    var api = window._TC && window._TC.customizerValidationAPI;
    if (typeof api === 'function') {
      try {
        api(true);
        return { ok: true };
      } catch (err) {
        return { ok: false, message: (err && err.message) || String(err) || 'Some fields are required' };
      }
    }
    var bad = firstIncompleteField(form);
    if (bad) return { ok: false, message: 'Please complete the required options above.' };
    return { ok: true };
  }

  /* --------------------------------------------------------------------- */
  /* 3. Error surface + disabled look                                       */
  /* --------------------------------------------------------------------- */

  function injectStyles() {
    if (document.getElementById('qikify-hoppy-bridge-styles')) return;
    var s = document.createElement('style');
    s.id = 'qikify-hoppy-bridge-styles';
    s.textContent =
      '.futureblink-booking-button.qh-incomplete,' +
      '.sticky-add-to-cart__button.qh-incomplete{opacity:.55;cursor:not-allowed;}' +
      '.qh-error{display:block;margin-top:10px;color:#ea4335;font-style:italic;line-height:1.4;}' +
      '.tcustomizer-field.qh-flash{outline:2px solid #ea4335;outline-offset:6px;border-radius:4px;}' +
      '.qh-hide-sticky{display:none!important;}';
    (document.head || document.documentElement).appendChild(s);
  }

  /* Qikify paints its own message right above the button when validation
     fails; showing ours as well would just say the same thing twice. */
  function qikifyErrorVisible(form) {
    var nodes = all('.tcustomizer-response, .tcustomizer-error', form)
      .concat(all('.tcustomizer-response, .tcustomizer-error'));
    for (var i = 0; i < nodes.length; i++) {
      if ((nodes[i].textContent || '').trim() && isVisible(nodes[i])) return true;
    }
    return false;
  }

  function showError(form, message) {
    /* Qikify paints asynchronously, so re-decide on the next few ticks and
       withdraw our copy as soon as its own message lands. */
    var render = function () {
      if (qikifyErrorVisible(form)) { clearError(form); return; }
      var anchor = form.querySelector(SEL.bookBtn) || form;
      var host = anchor.parentElement || form;
      var box = host.querySelector('.qh-error');
      if (!box) {
        box = document.createElement('span');
        box.className = 'qh-error';
        box.setAttribute('role', 'alert');
        host.appendChild(box);
      }
      box.textContent = message;
    };
    render();
    setTimeout(render, 250);
    setTimeout(render, 800);
  }

  function clearError(form) {
    all('.qh-error', form.parentElement || form).forEach(function (b) { b.remove(); });
  }

  function flashField(field) {
    if (!field) return;
    try { field.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { field.scrollIntoView(); }
    field.classList.add('qh-flash');
    setTimeout(function () { field.classList.remove('qh-flash'); }, 2000);
  }

  function refreshButtonState() {
    bridgedForms().forEach(function (form) {
      var incomplete = !!firstIncompleteField(form);
      var buttons = all(SEL.bookBtn, form).concat(all(SEL.stickyBtn));
      buttons.forEach(function (b) {
        b.classList.toggle('qh-incomplete', incomplete);
        if (incomplete) b.setAttribute('aria-disabled', 'true');
        else b.removeAttribute('aria-disabled');
      });
      if (!incomplete) clearError(form);
    });
  }

  /* --------------------------------------------------------------------- */
  /* 4. Arming: remember which selections belong to this booking            */
  /* --------------------------------------------------------------------- */

  var armed = null;

  function arm(form) {
    armed = {
      at: Date.now(),
      variantId: variantIdOf(form),
      snapshot: collectQikifyProps(form)
    };
    rememberPending(armed);
    log('armed', armed);
  }

  /*
   * Hoppy redirects straight to /cart after it adds the line, so the page that
   * held the selections is gone. Park a copy in sessionStorage; the next page
   * load repairs the line if the request-time merge did not happen (see
   * repairCart below).
   */
  var STORE_KEY = 'qhPendingOptions';

  function rememberPending(state) {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        at: state.at, variantId: state.variantId, props: state.snapshot
      }));
    } catch (e) {}
  }

  function readPending() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function clearPending() {
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) {}
  }

  function disarm(why) {
    if (armed) log('disarmed:', why);
    armed = null;
  }

  function isAppointmentVariant(id) {
    try {
      return !!(window._fbAppointmentVariants && window._fbAppointmentVariants.has(String(id)));
    } catch (e) { return false; }
  }

  function shouldMerge(item) {
    if (!armed) return false;
    if (Date.now() - armed.at > ARM_TTL_MS) { disarm('expired'); return false; }
    var id = item && item.id != null ? String(item.id) : null;
    if (!id) return true;                      /* no id to check against */
    if (armed.variantId && id === armed.variantId) return true;
    if (isAppointmentVariant(id)) return true; /* widget may swap the variant */
    return false;
  }

  /* Freshest possible read of the options at the moment of the cart add. */
  function propsForMerge() {
    var form = soleForm() || bridgedForms()[0];
    var live = form ? collectQikifyProps(form) : {};
    return Object.keys(live).length ? live : (armed ? armed.snapshot : {});
  }

  function applyProps(item) {
    if (!shouldMerge(item)) return false;
    var qik = propsForMerge();
    if (!Object.keys(qik).length) return false;

    var booking = item.properties || {};
    var merged = {};
    Object.keys(qik).forEach(function (k) { merged[k] = qik[k]; });
    /* Booking data (date, time, name, ...) always wins a key collision. */
    Object.keys(booking).forEach(function (k) { merged[k] = booking[k]; });
    item.properties = merged;
    log('merged options into cart line', item);
    return true;
  }

  /* --------------------------------------------------------------------- */
  /* 5. Intercept the cart add that the Confirm button triggers            */
  /* --------------------------------------------------------------------- */

  var CART_ADD = /\/cart\/add(\.js)?(\?|$)/;

  function isCartAdd(url) {
    if (!url) return false;
    try { return CART_ADD.test(String(url)); } catch (e) { return false; }
  }

  function transformJsonBody(text) {
    var data;
    try { data = JSON.parse(text); } catch (e) { return null; }
    var touched = false;
    if (data && Array.isArray(data.items)) {
      data.items.forEach(function (it) { if (applyProps(it)) touched = true; });
    } else if (data && typeof data === 'object') {
      if (applyProps(data)) touched = true;
    }
    if (!touched) return null;
    disarm('merged');
    clearPending();
    return JSON.stringify(data);
  }

  function transformFormLike(entriesGet, entriesSet) {
    /* FormData / URLSearchParams path, used by themes rather than Hoppy. */
    var id = entriesGet('id');
    if (!shouldMerge({ id: id })) return false;
    var qik = propsForMerge();
    var touched = false;
    Object.keys(qik).forEach(function (k) {
      var name = 'properties[' + k + ']';
      if (entriesGet(name) == null || entriesGet(name) === '') {
        entriesSet(name, qik[k]);
        touched = true;
      }
    });
    if (touched) { disarm('merged'); clearPending(); }
    return touched;
  }

  /* ---- fetch ---- */

  /*
   * Other apps on this store also wrap window.fetch, and at least one of them
   * grabs the native function early and re-assigns window.fetch later - which
   * silently drops any wrapper installed in between (this is exactly what made
   * the first version of this file do nothing). So: re-assert the wrapper on a
   * timer and before the booking starts, always wrapping whatever is currently
   * installed, so we stay the outermost link in the chain.
   */
  var myFetch = null;

  function installFetchPatch() {
    if (typeof window.fetch !== 'function') return;
    if (window.fetch === myFetch) return;
    var inner = window.fetch;

    myFetch = function (input, init) {
      try {
        var url = (input && input.url) ? input.url : input;
        var method = (init && init.method) || (input && input.method) || 'GET';

        if (isCartAdd(url) && String(method).toUpperCase() === 'POST' && armed) {
          var body = init && init.body;

          if (typeof body === 'string') {
            var next = transformJsonBody(body);
            if (next !== null) {
              init = Object.assign({}, init, { body: next });
              return inner.call(this, input, init);
            }
          } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
            transformFormLike(
              function (k) { return body.get(k); },
              function (k, v) { body.set(k, v); }
            );
          } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
            transformFormLike(
              function (k) { return body.get(k); },
              function (k, v) { body.set(k, v); }
            );
          }
        }
      } catch (e) {
        warn('fetch hook failed, passing through', e);
      }
      return inner.apply(this, arguments);
    };

    try { window.fetch = myFetch; } catch (e) { warn('could not install fetch hook', e); }
  }

  installFetchPatch();
  setInterval(installFetchPatch, 500);

  /* ---- XMLHttpRequest (belt and braces) ---- */
  var XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    var origOpen = XHR.prototype.open;
    var origSend = XHR.prototype.send;

    XHR.prototype.open = function (method, url) {
      this.__qhAdd = isCartAdd(url) && String(method || '').toUpperCase() === 'POST';
      return origOpen.apply(this, arguments);
    };

    XHR.prototype.send = function (body) {
      try {
        if (this.__qhAdd && armed) {
          if (typeof body === 'string' && body.charAt(0) === '{') {
            var next = transformJsonBody(body);
            if (next !== null) return origSend.call(this, next);
          } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
            transformFormLike(
              function (k) { return body.get(k); },
              function (k, v) { body.set(k, v); }
            );
          }
        }
      } catch (e) {
        warn('xhr hook failed, passing through', e);
      }
      return origSend.apply(this, arguments);
    };
  }

  /* --------------------------------------------------------------------- */
  /* 6. Gate the buttons                                                    */
  /* --------------------------------------------------------------------- */

  /*
   * Hoppy re-creates (clones) its Book now button on every re-render, so a
   * listener bound to the button itself would be thrown away. A capture-phase
   * listener on the document survives that and runs before Hoppy's own
   * handler, which is what lets us cancel the modal.
   */
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    var book = target.closest(SEL.bookBtn + ', ' + SEL.fab);
    if (book) {
      var form = formFor(book);
      if (!form) return; /* a FAB with no Qikify product in context: leave it */

      var result = validate(form);
      if (!result.ok) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showError(form, result.message);
        flashField(firstIncompleteField(form));
        refreshButtonState();
        log('blocked booking:', result.message);
        return;
      }
      clearError(form);
      installFetchPatch();
      arm(form);
      return;
    }

    /*
     * The theme's sticky bar sits outside the product form, so Hoppy never
     * hides it and it would add the product with no booking at all. Route it
     * through the booking button instead (which re-enters the gate above).
     */
    var sticky = target.closest(SEL.stickyBtn);
    if (sticky) {
      var sForm = soleForm();
      if (!sForm) return;
      var bookBtn = sForm.querySelector(SEL.bookBtn);
      if (!bookBtn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      bookBtn.click();
    }
  }, true);

  /* --------------------------------------------------------------------- */
  /* 7. Keep everything in sync                                             */
  /* --------------------------------------------------------------------- */

  /*
   * The theme binds its sticky Add to cart bar before this file loads, so its
   * handler runs first and a capture-phase block cannot reliably stop it - it
   * added the product with no booking attached. On appointment products the
   * bar is therefore hidden outright; Hoppy's own floating Book now button
   * already provides the same always-visible CTA. Every other product keeps
   * the sticky bar exactly as before.
   */
  function hideStickyOnBookingProducts() {
    if (!soleForm()) return;
    all('sticky-add-to-cart, .sticky-add-to-cart, .sticky-add-to-cart__bar').forEach(function (el) {
      el.classList.add('qh-hide-sticky');
    });
  }

  var syncTimer = null;
  function sync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      injectStyles();
      hideStickyOnBookingProducts();
      refreshButtonState();
    }, 250);
  }

  document.addEventListener('change', sync, true);
  document.addEventListener('input', sync, true);

  /* Booking modal closed without confirming -> forget the armed selections. */
  var modalWasOpen = false;
  setInterval(function () {
    var open = !!document.getElementById('futureblink-embed');
    if (modalWasOpen && !open && armed) disarm('modal closed');
    modalWasOpen = open;
  }, 1000);

  /* --------------------------------------------------------------------- */
  /* 8. Backstop: repair the line after the fact                            */
  /* --------------------------------------------------------------------- */

  /*
   * If the request-time merge was missed for any reason, the options are still
   * parked in sessionStorage. On the next page load - normally /cart, where
   * Hoppy sends the customer - find the booking line that is missing them and
   * put them on it with /cart/change.js.
   */
  function repairCart() {
    var pending = readPending();
    if (!pending || !pending.props) return;
    if (Date.now() - pending.at > ARM_TTL_MS) { clearPending(); return; }

    var visible = Object.keys(pending.props).filter(function (k) { return k.charAt(0) !== '_'; });
    if (!visible.length) { clearPending(); return; }

    window.fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) return;

        var target = null;
        cart.items.forEach(function (item) {
          if (pending.variantId && String(item.variant_id) !== String(pending.variantId)) return;
          var have = item.properties || {};
          var missing = visible.some(function (k) { return !(k in have); });
          if (missing) target = item; /* last match wins: the newest line */
        });

        if (!target) { clearPending(); return; }

        var merged = {};
        Object.keys(pending.props).forEach(function (k) { merged[k] = pending.props[k]; });
        Object.keys(target.properties || {}).forEach(function (k) { merged[k] = target.properties[k]; });

        /* Clear first so a failure can never turn into a reload loop. */
        clearPending();
        log('repairing cart line', target.key, merged);

        return window.fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: target.key, quantity: target.quantity, properties: merged })
        }).then(function () {
          if (/\/cart/.test(window.location.pathname)) window.location.reload();
        });
      })
      .catch(function (e) { warn('cart repair failed', e); clearPending(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sync(); repairCart(); });
  } else {
    sync();
    repairCart();
  }

  new MutationObserver(sync).observe(document.documentElement, { childList: true, subtree: true });

  log('installed');
})();
